import { z } from "zod";
import type {
  Deck,
  DeckLanguages,
  DeckSummary,
  LanguageInfo,
  Manifest,
  Word,
} from "@/types/deck";

/**
 * Runtime validation for everything the app reads out of `data/`. The
 * hand-written interfaces in `types/deck.ts` stay the source of truth; these
 * schemas mirror them, and the compile-time assertions at the bottom of this
 * file fail the build if the two ever drift.
 *
 * Deliberately strict about the things that silently break the UI (colors,
 * locales, relative paths, revision hashes) and deliberately lax about unknown
 * object keys: the contract says `schemaVersion` is bumped only on breaking
 * changes, so an additive field from a newer pipeline must not break an older
 * client. Unknown keys are stripped, not rejected - the same reason a bank's
 * unknown columns are ignored rather than refused.
 */

/** The only `schemaVersion` this client understands. */
export const DATA_SCHEMA_VERSION = 2;

/**
 * Refuses data from a different contract instead of guessing at it. Typed as
 * `number` rather than a literal so the schema output stays assignable to the
 * interfaces, which declare `schemaVersion: number`.
 */
const schemaVersion = z
  .number()
  .int("schemaVersion must be an integer")
  // The `: boolean` annotation keeps zod's output type `number` (matching the
  // interfaces) instead of letting it narrow to the literal `2`.
  .refine((value): boolean => value === DATA_SCHEMA_VERSION, {
    message: `unsupported schemaVersion: this build understands ${DATA_SCHEMA_VERSION} only`,
  });

/**
 * A revision is the first 12 hex characters of a sha256 over content, e.g.
 * `3ab8f10c92d4`. Checking the shape is worth it because a revision is a cache
 * key: anything that is not a content hash (a number, `null`, a truncated
 * string) would quietly key a deck under a value that never changes, and the
 * app would then serve a stale deck forever.
 */
const contentRevision = z
  .string()
  .regex(/^[0-9a-fA-F]{12}$/, "expected a 12-character hex content revision");

/** `#RRGGBB`, per the data contract. */
const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "expected a #RRGGBB color");

/**
 * BCP-47 language tag, loose enough for real tags ("nl", "nl-NL",
 * "zh-Hant-TW") and tight enough to catch a label pasted into a locale field.
 */
const locale = z
  .string()
  .regex(
    /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{1,8})*$/,
    "expected a BCP-47 locale tag",
  );

/** Slug-shaped, stable identifier (deck ids and word ids). */
const slugId = z
  .string()
  .min(1, "must not be empty")
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "expected a lowercase slug");

const nonEmptyText = z.string().min(1, "must not be empty");

const languageInfoSchema = z.object({
  label: nonEmptyText,
  locale,
});

const deckLanguagesSchema = z.object({
  front: languageInfoSchema,
  back: languageInfoSchema,
});

/**
 * One row of a bank, after the TSV has been split into named columns and `tags`
 * has been split on commas. `front` and `back` are checked for presence only:
 * their inline formatting is the renderer's business and can never be invalid
 * (unbalanced markup renders literally, by contract).
 */
export const bankRowSchema = z.object({
  id: slugId,
  front: nonEmptyText,
  back: nonEmptyText,
  // May be empty, never absent.
  tags: z.array(nonEmptyText),
});

const deckSummarySchema = z.object({
  id: slugId,
  name: nonEmptyText,
  color: hexColor,
  languages: deckLanguagesSchema,
  wordCount: z.number().int().nonnegative(),
  revision: contentRevision,
  // Deliberately just a string: the set of renderable icons is a frontend
  // concern, and an unknown name degrades to the fallback icon rather than
  // rejecting the whole manifest.
  icon: nonEmptyText.optional(),
});

export const manifestSchema = z.object({
  schemaVersion,
  decks: z.array(deckSummarySchema),
});

/**
 * A whole assembled deck. Not applied at runtime - `parseBank` validates row by
 * row so that a bad row can be reported with its line number, and the rest of
 * the deck comes from an already-validated manifest entry. It exists so that
 * `Deck` is covered by the drift assertions below like every other shape.
 */
const deckSchema = z.object({
  schemaVersion,
  id: slugId,
  name: nonEmptyText,
  color: hexColor,
  revision: contentRevision,
  languages: deckLanguagesSchema,
  words: z.array(bankRowSchema),
});

/**
 * Compile-time proof that each schema produces exactly its interface. If a
 * schema gains, loses or retypes a field without the interface following (or
 * vice versa), one of these aliases stops satisfying `Expect<true>` and tsc
 * fails here rather than somewhere three components deep.
 */
type Extends<A, B> = [A] extends [B] ? true : false;
type Mutual<A, B> = Extends<A, B> extends true ? Extends<B, A> : false;
type Expect<T extends true> = T;

export type LanguageInfoSchemaMatches = Expect<
  Mutual<z.output<typeof languageInfoSchema>, LanguageInfo>
>;
export type DeckLanguagesSchemaMatches = Expect<
  Mutual<z.output<typeof deckLanguagesSchema>, DeckLanguages>
>;
export type BankRowSchemaMatches = Expect<
  Mutual<z.output<typeof bankRowSchema>, Word>
>;
export type DeckSchemaMatches = Expect<
  Mutual<z.output<typeof deckSchema>, Deck>
>;
export type DeckSummarySchemaMatches = Expect<
  Mutual<z.output<typeof deckSummarySchema>, DeckSummary>
>;
export type ManifestSchemaMatches = Expect<
  Mutual<z.output<typeof manifestSchema>, Manifest>
>;
